import { format } from 'date-fns';

/**
 * Take a Date produced by a local picker (local-tz components carry the user's
 * intended civil year/month/day) and produce a UTC-midnight Date. Used so all
 * civil-date arithmetic and rendering reads UTC components, eliminating
 * timezone-introduced off-by-ones.
 */
export function localToCivilUtc(date: Date | null): Date | null {
  if (date === null) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/**
 * Convert a stored instant (timestamptz from the API, or an already-civil
 * UTC-midnight Date) to its civil date by reading **UTC** components. Use this
 * for server-sourced values so the viewer's timezone can't shift the date;
 * {@link localToCivilUtc} is only for Dates produced by local pickers.
 */
export function instantToCivilUtc(date: Date | string | null): Date | null {
  if (date === null) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Format a UTC-midnight civil-date Date by treating its UTC components as
 * local. Pairs with {@link localToCivilUtc}.
 */
export function formatCivilDate(date: Date, pattern: string): string {
  const display = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return format(display, pattern);
}

/**
 * Combine a UTC-midnight civil date with a "HH:mm" string into a naive
 * "YYYY-MM-DDTHH:mm:ss" string suitable for posting to the backend's
 * `startDateTime`/`endDateTime` fields (resolved against the event's
 * `timeZone`).
 */
export function combineDateAndTime(date: Date, time: string): string {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  const datePart = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const timePart = /^\d{2}:\d{2}$/.test(time) ? `${time}:00` : '00:00:00';
  return `${datePart}T${timePart}`;
}

/**
 * Inverse of {@link combineDateAndTime}: pull the civil date (UTC-midnight)
 * and "HH:mm" time out of a stored naive timestamp.
 */
export function splitDateAndTime(value: Date | string | null): { date: Date | null; time: string } {
  if (value === null) return { date: null, time: '' };
  const s = typeof value === 'string' ? value : value.toISOString();
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
  if (!match) return { date: null, time: '' };
  const [, y, mo, d, h, mi] = match;
  const civil = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  const time = h === '00' && mi === '00' ? '' : `${h}:${mi}`;
  return { date: civil, time };
}
